import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useAtom } from "jotai";
import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";
import {
  DATASET_MODES,
  DEFAULT_ADVANCED_SETUP,
  allowedDatasetModes,
  datasetModeAtom,
  effectiveDatasetMode,
  type DatasetModeName,
} from "../datasetMode";
import { useDatasetModeAccess } from "../hooks/useDatasetMode";
import { AdvancedSetupDialog } from "./AdvancedSetupDialog";

/** The Dataset builder mode, chosen in the chat box. Each message sends it. Hidden when the account allows only one mode. */
export const DatasetModePicker = () => {
  const [state, setState] = useAtom(datasetModeAtom);
  const access = useDatasetModeAccess();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = effectiveDatasetMode(state, access);
  const modes = allowedDatasetModes(access);
  const label = DATASET_MODES.find((mode) => mode.id === current)?.label ?? current;

  const selectMode = (value: string) => {
    // Advanced takes effect when its dialog is saved.
    if (value === "advanced") {
      setAdvancedOpen(true);
      return;
    }
    setState((previous) => ({ ...previous, mode: value as DatasetModeName, chosen: true }));
  };

  if (modes.length < 2) return null;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={`Dataset mode: ${label}`}
            title="Dataset mode"
            className="inline-flex items-center gap-1 text-zinc-400 transition-colors hover:text-zinc-700 data-[state=open]:text-zinc-700"
          >
            {label}
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="top"
          collisionPadding={8}
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-72 overflow-y-auto"
          onCloseAutoFocus={(event) => {
            if (advancedOpen) event.preventDefault();
          }}
        >
          <DropdownMenuLabel>Dataset mode</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={current} onValueChange={selectMode}>
            {modes.map((mode) => (
              <DropdownMenuRadioItem
                key={mode.id}
                value={mode.id}
                disabled={!mode.available}
              >
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-zinc-900">{mode.label}</span>
                    {mode.time && (
                      <span className="text-xs tabular-nums text-zinc-500">{mode.time}</span>
                    )}
                  </span>
                  <span className="text-xs leading-5 text-zinc-500">
                    {mode.description}
                  </span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {access.mapModels && (
        <AdvancedSetupDialog
          open={advancedOpen}
          onOpenChange={setAdvancedOpen}
          value={{ ...DEFAULT_ADVANCED_SETUP, ...state.advanced }}
          onSave={(advanced) => setState({ mode: "advanced", advanced, chosen: true })}
          returnFocusRef={triggerRef}
        />
      )}
    </>
  );
};
